import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId, UserRole } from '../common.js';
/**
 * API key for external integrations (AG-UI streaming endpoint + MCP service).
 *
 * Firestore path: tenants/_platform/api_keys/{keyId}
 *
 * Wire shape:
 *   id            — Firestore doc id (also encoded into the key prefix metadata).
 *   prefix        — `kb_live_` or `kb_test_` — the only bit of the plaintext
 *                   key we keep in cleartext, used by the auth middleware to
 *                   bucket-load candidate hashes before doing the Argon2id
 *                   verify. NEVER persist the suffix.
 *   keyPreview    — last 4 chars of the plaintext suffix, prepended with `…`
 *                   for the admin UI to identify a key without revealing it.
 *   hash          — Argon2id hash of the full plaintext token (prefix +
 *                   suffix). The plaintext is returned ONCE at create time
 *                   and never recoverable afterwards.
 *   role          — the role the agent will execute under when this key
 *                   calls the AG-UI endpoint. kb_admin only can ISSUE keys
 *                   but may choose any role for the key itself.
 *   tenantId      — the tenant the key will write under (defaults to _platform).
 *   rpmLimit      — per-key rate limit in requests per minute. Default 60.
 *   status        — active | revoked. Rotation = revoke old + create new with
 *                   same label.
 *   label         — admin-supplied name (e.g. "marketing-prod", "mcp-service").
 *   createdAt / createdBy / revokedAt / revokedBy / lastUsedAt — audit.
 */
export const ApiKeyStatus = z.enum(['active', 'revoked']);
export const ApiKeyEnv = z.enum(['live', 'test']);
export const ApiKeyRecord = z.object({
    id: DocId,
    label: z.string().min(1).max(120),
    prefix: z.string().regex(/^kb_(live|test)_$/),
    keyPreview: z.string().min(4).max(16),
    hash: z.string().min(20).max(512),
    role: UserRole,
    tenantId: TenantId,
    rpmLimit: z.number().int().positive().max(10_000),
    status: ApiKeyStatus,
    createdAt: IsoTimestamp,
    createdBy: DocId,
    revokedAt: IsoTimestamp.optional(),
    revokedBy: DocId.optional(),
    lastUsedAt: IsoTimestamp.optional(),
});
/**
 * Safe projection for admin UI list endpoints — strips the hash + key
 * material entirely. Only the preview + audit fields are exposed.
 */
export const ApiKeyPublic = ApiKeyRecord.omit({ hash: true });
/**
 * Create-key request from the admin UI. kb_admin only at the route layer.
 */
export const ApiKeyCreateInput = z.object({
    label: z.string().min(1).max(120),
    role: UserRole,
    rpmLimit: z.number().int().positive().max(10_000).optional(),
});
/**
 * Patch input — only rotate or revoke, plus rpmLimit tuning.
 * `action: 'rotate'` returns a new plaintext suffix; the old hash is
 * cleared in the same write so the cutover is instant (no grace period).
 * `action: 'revoke'` flips status to revoked.
 */
export const ApiKeyPatchInput = z.discriminatedUnion('action', [
    z.object({ action: z.literal('revoke') }),
    z.object({ action: z.literal('rotate') }),
    z.object({
        action: z.literal('update'),
        rpmLimit: z.number().int().positive().max(10_000),
    }),
]);
/**
 * One-shot create response. `plaintext` is the only time the full token
 * value is ever returned. Admin UI must surface a "save this now" modal.
 */
export const ApiKeyCreateResponse = z.object({
    key: ApiKeyPublic,
    plaintext: z.string().min(20).max(200),
});
/**
 * Per-month usage rollup row read from the BigQuery `api_key_usage_monthly`
 * view. Surfaced in the admin UI next to each key.
 */
export const ApiKeyUsageRow = z.object({
    apiKeyId: DocId,
    month: z.string().regex(/^\d{4}-\d{2}$/),
    callCount: z.number().int().nonnegative(),
    lastCallAt: IsoTimestamp.optional(),
});
//# sourceMappingURL=api-key.js.map